import { sortBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useNavigationType, NavigationType } from 'react-router';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { keepPreviousData } from '@tanstack/react-query';

import { Market, Outcome, PublishedEventListItem, Scoreboard, TopMarketType } from '../../@types';

import { QUERY_KEYS, SECOND, USED_FOR } from '../../constants';
import { MessageBetType } from '../../helpers/betslipMessageParser';
import topMarketsUpdater from '../../helpers/eventTopMarketsUpdater';
import { buildInPlayVirtuosoItems } from '../../utils/virtuoso/inPlayVirtuosoBuilder';

import { addOrRemoveBetslipBet } from '../../stores/betslip/selectors';
import { createBetslipSelection } from '../../utils/betslipSelection';
import { useMiniStatsWidget } from '../../contexts/MiniStatsWidgetContext';
import useInfiniteScroll from '../useInfiniteScroll';
import { useMarketCategories } from '../useMarketCategories';
import useMarketGroupStorage from '../useMarketGroupStorage';
import { useUpdaterCallback } from '../useUpdaterCallback';
import useVirtuosoScrollRestoration from '../useVirtuosoScrollRestoration';
import useWebsocket, { MessageType } from '../useWebsocket';
import { useGetEventScoreboard, useSportById, useSports } from '../../queries';

type UseInPlayPageOptions = {
  competitionIds?: string[];
};

export const useInPlayPage = ({ competitionIds }: UseInPlayPageOptions = {}) => {
  const { sportId } = useParams();
  const navigate = useNavigate();
  const { toggle } = useMiniStatsWidget(null);
  const enableMarketGroups = useFeatureIsOn('enable-tournament-market-groups');

  const [events, setEvents] = useState<PublishedEventListItem[]>([]);
  const [topMarkets, setTopMarkets] = useState<Record<string, Market[] | null>>({});
  const [selectedEvent, setSelectedEvent] = useState<PublishedEventListItem | null>(null);
  const [scoreboardState, setScoreboardState] = useState<Scoreboard | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const selectedEventRef = useRef<PublishedEventListItem | null>(null);
  const scoreboardStateRef = useRef<Scoreboard | null>(null);
  const prevSportIdRef = useRef(sportId);

  const { data: groupCategories } = useMarketCategories(enableMarketGroups, USED_FOR.IN_PLAY, sportId);
  const defaultMarketGroupSelected = useMemo(() => groupCategories?.[0]?.marketTypeGroups?.[0], [groupCategories]);
  const { selectedData, handleSelectedData } = useMarketGroupStorage(
    sportId,
    enableMarketGroups,
    defaultMarketGroupSelected,
    groupCategories
  );

  const {
    items: publishedEventsData,
    isLoading: isEventsLoading,
    isSuccess,
    topMarketTypes,
    loadMore,
    isFetchingNextPage,
    isPreviousData,
  } = useInfiniteScroll<PublishedEventListItem & { topMarketTypes: TopMarketType[] }>({
    queryKey: [QUERY_KEYS.publishedEvents, sportId, selectedData?.id, competitionIds],
    endpoint: 'events/published',
    queryParams: {
      sportId,
      isActive: true,
      isLive: true,
      limit: 25,
      marketTypeGroupId: selectedData?.id ?? defaultMarketGroupSelected?.id,
      ...(competitionIds?.length ? { competitionIds } : {}),
      ...(searchValue.trim() ? { search: searchValue.trim() } : {}),
    },
    enabled: !!sportId,
    refetchInterval: 20 * SECOND,
    placeholderData: keepPreviousData,
  });

  const {
    data: sportsData,
    isLoading: isSportsLoading,
    isFetching: isSportsFetching,
  } = useSports({ queryParams: { isLive: true } });
  const { data: sport } = useSportById(sportId);
  const {
    data: scoreboard,
    refetch: refetchScoreboard,
    isLoading: isScoreboardLoading,
  } = useGetEventScoreboard(selectedEvent?.id || '', {});

  const updaterCallback = useUpdaterCallback(
    setEvents,
    setSelectedEvent,
    selectedEventRef,
    scoreboardStateRef,
    setScoreboardState,
    refetchScoreboard
  );
  const { joinRoom, resetWS } = useWebsocket<MessageType<MessageBetType>>({ callback: updaterCallback });

  const topMarketsUpdaterCallback = useCallback((data: Record<string, MessageType<MessageBetType>>[]) => {
    setTopMarkets((prevState) => {
      let newTopMarkets = prevState;
      data.forEach((message) => {
        const updatedEventId = Object.keys(message)[0];
        const marketsToUpdate = newTopMarkets[updatedEventId];
        const updatedMarkets = topMarketsUpdater(marketsToUpdate, message[updatedEventId]);

        if (updatedMarkets !== marketsToUpdate) {
          newTopMarkets = { ...newTopMarkets, [updatedEventId]: updatedMarkets };
        }
      });
      return newTopMarkets;
    });
  }, []);
  const { joinRoom: joinTopMarketRoom, resetWS: resetTopMarketWS } = useWebsocket<MessageType<MessageBetType>>({
    callback: topMarketsUpdaterCallback,
  });

  const isInitialLoad = isEventsLoading && !publishedEventsData?.length;
  const hasNoEvents = isSuccess && !publishedEventsData?.length;
  // Query data is available but hasn't been synced to events state yet (useEffect delay)
  const isEventsSyncing = isSuccess && !!publishedEventsData?.length && !events.length;
  const outcomeTypes = useMemo(() => sortBy(topMarketTypes?.[0]?.outcomeTypes ?? [], ['position']), [topMarketTypes]);
  const virtuosoItems = useMemo(() => buildInPlayVirtuosoItems(events, topMarkets), [events, topMarkets]);

  const scrollKey = useMemo(() => `in-play:${sportId ?? 'all'}`, [sportId]);
  const { virtuosoRef, onRangeChanged, restoreStateFrom, isRestoring } = useVirtuosoScrollRestoration(scrollKey);
  const navigationType = useNavigationType();
  const shouldAttemptRestore = navigationType === NavigationType.Pop;
  // Keep skeleton until events state is populated to avoid empty-state flash
  const showSkeleton = isInitialLoad || isEventsSyncing || (shouldAttemptRestore && isRestoring);

  useEffect(() => {
    if (sportId && prevSportIdRef.current !== sportId) {
      prevSportIdRef.current = sportId;
      resetWS();
      resetTopMarketWS();
      setSelectedEvent(null);
    }
  }, [sportId, resetWS, resetTopMarketWS]);

  useEffect(() => {
    if (isSuccess && publishedEventsData?.length && !selectedEvent) {
      const firstEventWithScoreboard = publishedEventsData.find((event) => !!event.mainEventPartScore);
      if (firstEventWithScoreboard) {
        setSelectedEvent(firstEventWithScoreboard);
      }
    }
  }, [isSuccess, publishedEventsData, selectedEvent]);

  useEffect(() => {
    if (!isSuccess) return;

    const topMarketsData: Record<string, Market[] | []> = {};

    publishedEventsData?.forEach((event) => {
      joinRoom(`${event.id}_reduced`);
      if (event?.topMarkets?.length) {
        event.topMarkets.forEach((market) => {
          joinTopMarketRoom(`${event.id}_${market.marketType.id}`);
          topMarketsData[event.id] = [...(topMarketsData[event.id] || []), market];
        });
      }
    });

    setEvents(publishedEventsData || []);
    setTopMarkets(topMarketsData);
  }, [isSuccess, publishedEventsData, joinRoom, joinTopMarketRoom]);

  useEffect(() => {
    if (scoreboard) setScoreboardState(scoreboard);
  }, [scoreboard]);

  useEffect(() => {
    selectedEventRef.current = selectedEvent;
  }, [selectedEvent]);

  useEffect(() => {
    scoreboardStateRef.current = scoreboardState;
  }, [scoreboardState]);

  useEffect(() => {
    if (selectedEvent?.id) {
      const updatedSelectedEvent = events.find((event) => event.id === selectedEvent.id);
      setSelectedEvent(updatedSelectedEvent ?? null);
    }
  }, [events, selectedEvent?.id]);

  const handleAddToBetslip = useCallback((event: PublishedEventListItem, topMarket: Market, outcome: Outcome) => {
    if (!topMarket) return;
    addOrRemoveBetslipBet(createBetslipSelection({ event, market: topMarket, outcome }));
  }, []);

  const handleIsScrolling = useCallback(
    (isScrolling: boolean) => {
      if (isScrolling) toggle(null);
    },
    [toggle]
  );

  useEffect(() => {
    if (!sportId && sportsData?.length) {
      navigate(`/in-play/sport/${sportsData[0].id}`, { replace: true });
    }
  }, [sportId, sportsData, navigate]);

  return {
    sportId,
    sport,
    sportsData: sportsData || [],
    isSportsLoading,
    isSportsFetching,
    events,
    topMarkets,
    selectedEvent,
    setSelectedEvent,
    scoreboardState,
    isScoreboardLoading,
    groupCategories,
    selectedData,
    handleSelectedData,
    searchValue,
    setSearchValue,
    outcomeTypes,
    topMarketTypes,
    virtuosoItems,
    virtuosoRef,
    onRangeChanged,
    restoreStateFrom,
    loadMore,
    isFetchingNextPage,
    isPreviousData,
    showSkeleton,
    hasNoEvents,
    handleAddToBetslip,
    handleIsScrolling,
  };
};
